// ** React Imports
import { useEffect, useMemo, memo, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { getUsers } from "store/modules/main-pages/users/get-users.action";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import Helmet from "react-helmet";
import md5 from "md5";
import loadable from "@loadable/component";
import withHydrationOnDemand from "react-hydration-on-demand";

// ** Custom Components
import { CardWrapper } from "components/layout/Card/CardWrapper";
import { CardTitle } from "components/layout/Card/CardTitle";
import { CardBody } from "components/layout/Card/CardBody";
import { CardsBlockWrapper } from "components/layout/Card/CardsBlockWrapper";
import Heading from "components/typography/Heading";
import ScrollToTop from "components/global/ScrollToTop";
import EmptyBlock from "components/layout/Block/BlockEmpty";
import useUsersSeoMetaTags from "./hooks/useUsersSeoMetaTags";
import UsersListSeo from "./components/UsersListSeo";
import Block from "components/layout/Block";
const UsersSeoNavPanelTabsSelectors = loadable(
	() => import("./components/UsersSeoNavPanelTabsSelectors"),
);
const Breadcrumbs = loadable(() => import("../Users/components/Breadcrumbs"));
const BreadcrumbsWithHydrationOnDemand = withHydrationOnDemand({
	on: ["idle"],
	onBefore: Breadcrumbs.load,
})(Breadcrumbs);

// ** Hooks
import useUsersSeoValidator from "./hooks/useUsesSeoValidator";
import useUsersSeoApiParams from "./hooks/useUsersSeoApiParams";

// ** Utils & Helpers
import formatMessage from "utils/formatMessage";
import pseudoFormat from "utils/pseudoFormat";

// ** Custom Filters
import { compute as filterSexCompute } from "hooks/filters/useFilterSexSeo";
import { compute as filterPreferencesCompute } from "hooks/filters/useFilterPreferencesSeo";

// #####################################################

// ** SEO_TESTS
const LOCATIONS_WITHOUT_SEO_PREFERENCES_PAGES = [
	"radom",
	"bytom",
	"gliwice",
	"gdynia",
	"ostrow-wielkopolski",
	"pabianice",
	"tczew",
	"sanok",
	"ciechanow",
	"krosno",
];

// #####################################################

const UsersSeo = ({ match }) => {
	const { t } = useTranslation(["common", "pages", "filtersAndSelectors"]);

	const dispatch = useDispatch();

	const isAuth = useSelector((state) => state.globalCommon.isAuth);

	const {
		country: countryUrl,
		region: regionUrl,
		city: cityUrl,
		filterUrl,
	} = match?.params || {};

	const locationMatchUrl = match?.url || "";

	const history = useHistory();

	const usersError = useSelector((state) => state.mainPageUsers.error);
	const users = useSelector((state) => state.mainPageUsers);
	const locationMd5HashFromState = useSelector(
		(state) => state.mainPageUsers.locationMd5Hash,
	);

	// ** Sprawdzenie poprawności parametrów w query
	const { isLocationValid, isFilterValid } = useUsersSeoValidator({
		countryUrl,
		regionUrl,
		cityUrl,
		filterUrl,
	});

	const { getApiParams } = useUsersSeoApiParams({
		countryUrl,
		regionUrl,
		cityUrl,
		filterUrl,
	});

	const { title, metaDescription, ogMetaTags, canonicalLink } =
		useUsersSeoMetaTags({
			filterUrl,
			locationPathname: locationMatchUrl,
		});

	const locationCity = useSelector(
		(state) => state.mainPageUsers.location.city,
	);
	const locationRegion = useSelector(
		(state) => state.mainPageUsers.location.region,
	);
	const locationCountry = useSelector(
		(state) => state.mainPageUsers.location.country,
	);
	const language = useSelector((state) => state.globalCommon.language);

	const sex = filterSexCompute(false).urlToApiParam(filterUrl);
	const isSexFilterValid = sex > 0;

	const preferences = filterPreferencesCompute.urlToApiParam(filterUrl);
	const isPreferencesFilterValid =
		Number.isInteger(preferences) && preferences > 0;

	const headingText = useMemo(() => {
		if (isSexFilterValid) {
			return `${t(`common:sexOptions:${filterUrl}`)} ${
				locationCity || locationRegion || locationCountry || ""
			} - Sex Randki`;
		}

		if (isPreferencesFilterValid) {
			return `${t(`filtersAndSelectors:preferences:${preferences}`)} ${
				locationCity || locationRegion || locationCountry || ""
			} - Sex Randki`;
		}

		return `${
			locationCity || locationRegion || locationCountry || ""
		} - Sex Randki`;
	}, [isAuth, locationMatchUrl, language]);

	const heading2Text = useMemo(() => {
		const foundUsersCount = pseudoFormat(users.findnumber);

		return formatMessage(t(`common:metaDescription`), {
			foundUsersCount,
			locationCity,
		});
	}, [isAuth, locationCity, language]);

	const containerRef = useRef(null);
	const triggerObserver = useRef();
	const triggerRef = useCallback(
		(node) => {
			if (users.loading || !("IntersectionObserver" in window)) return;
			if (users.pagination.currentpage >= users.pagination.totalpages)
				return null;
			if (triggerObserver.current) triggerObserver.current.disconnect();

			const marginValue = Math.min(
				containerRef.current?.getBoundingClientRect().height * 0.15 ||
					600,
				800,
			);

			triggerObserver.current = new IntersectionObserver(
				(entries) => {
					if (
						entries.some((entry) => entry.isIntersecting) &&
						!users.loading
					) {
						const apiParams = getApiParams({ isAuth });
						dispatch(
							getUsers({
								apiParams: {
									...apiParams,
									page: users.pagination.currentpage + 1,
								},
								isAuth,
								useUrl: true,
								locationMd5Hash: "",
								infinityScroll: true,
							}),
						);
					}
				},
				{ rootMargin: `0px 0px ${marginValue}px 0px` },
			);
			if (node) triggerObserver.current.observe(node);
		},
		[
			users.loading,
			users?.pagination?.currentpage,
			users?.pagination?.totalpages,
		],
	);

	useEffect(() => {
		const actualLocationMd5Hash = md5(`${locationMatchUrl}`);

		if (actualLocationMd5Hash !== locationMd5HashFromState) {
			const apiParams = getApiParams();

			dispatch(
				getUsers({
					apiParams,
					isAuth,
					useUrl: true,
					locationMd5Hash: actualLocationMd5Hash,
				}),
			);
		}
	}, [locationMatchUrl]);

	// #####################################################

	// ** Przekierowanie na stronę błędu w przypadku nieprawidłowych parametrów
	if (
		!isLocationValid() ||
		!isFilterValid() ||
		usersError ||
		LOCATIONS_WITHOUT_SEO_PREFERENCES_PAGES.includes(cityUrl)
	) {
		history.replace("/error404");
		return null;
	}

	return (
		<>
			{/* Sekcja HEAD */}
			<Helmet title={title}>
				<meta name="description" content={metaDescription} />
				{ogMetaTags}
				{canonicalLink()}
			</Helmet>

			<ScrollToTop />

			<CardsBlockWrapper>
				{/* Sekcja Filtrów */}
				<CardWrapper>
					<CardTitle>
						<Heading type="h1">{headingText}</Heading>
					</CardTitle>
					<CardBody>
						<Block flex withRowGap={"0.5rem"} column>
							<Heading type="h2" size="1.1rem">
								{heading2Text}
							</Heading>

							<BreadcrumbsWithHydrationOnDemand
								locationPath={
									"/search/:country?/:region?/:city?"
								}
								locationChipsVisible
								countryUrl={countryUrl}
								regionUrl={regionUrl}
								cityUrl={cityUrl}
								withOtherCities={false}
							/>

							<Block>
								<UsersSeoNavPanelTabsSelectors
									countryUrl={countryUrl}
									regionUrl={regionUrl}
									cityUrl={cityUrl}
									filterUrl={filterUrl}
									locationPathname={locationMatchUrl}
								/>
							</Block>
						</Block>
					</CardBody>
				</CardWrapper>

				{/* Główna zawartość strony */}
				<UsersListSeo
					triggerRef={triggerRef}
					containerRef={containerRef}
					apiParams={getApiParams({ isAuth })}
					isAuth={isAuth}
					currentpage={users.pagination.currentpage}
					cityUrl={cityUrl}
				/>

				<EmptyBlock />
			</CardsBlockWrapper>
		</>
	);
};

// #####################################################

export default memo(UsersSeo, (prevProps, nextProps) => {
	return prevProps?.match?.url === nextProps?.match?.url;
});

// #####################################################
